import { useMutation, useQuery } from "@apollo/client";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import Card from "../../components/Card";
import Spinner from "../../components/Spinner";
import { useToast } from "../../components/Toast/toastHook";
import { hydrateOpenHouseRows } from "../../utils/openHouseForm";
import ListingForm from "../../components/listings/ListingForm";
import { GET_COMPANY_USERS } from "../../graphql/companyQueries";
import {
  GET_COMPANY_LISTINGS,
  GET_LISTING_BY_ID,
  UPDATE_LISTING,
} from "../../graphql/listingQueries";
import { useListings } from "../../context/ListingContext";

const UpdateListing = () => {
  const { companyId, listingId } = useParams();
  const navigate = useNavigate();

  // Get current user to check if agent
  const currentUser = JSON.parse(localStorage.getItem("userInfo"));
  const isAgentRole = currentUser?.role?.name === "AGENT";

  const [agents, setAgents] = useState([]);

  const [initialValues, setInitialValues] = useState({
    streetAddr: "",
    city: "",
    state: "",
    zip: "",
    beds: "",
    baths: "",
    yearBuilt: "",
    price: "",
    sqFt: "",
    lotSize: parseInt(),
    acreage: parseInt(),
    notes: "",
    expiration: new Date(),
    buyer_commission: "",
    company: companyId,
    user: "",
    office: "",
    imageURLs: [],
    amenities: [],
    links: [],
  });

  const {
    isOpenHouse,
    setOpenHouses,
    setIsOpenHouse,
    getFormattedOpenHouses,
    resetOpenHouses,
  } = useListings();

  const {
    loading: listingLoad,
    data: listingData,
    error: listingError,
  } = useQuery(GET_LISTING_BY_ID, {
    variables: {
      listingId,
    },
    fetchPolicy: "no-cache",
    onError(e) {
      toast("error", "There was an error loading the listing data");
    },
  });

  const {
    loading: agentLoad,
    data: agentData,
    error: agentError,
  } = useQuery(GET_COMPANY_USERS, {
    variables: {
      companyId,
    },
    // fetchPolicy: "no-cache",
  });

  const toast = useToast(4000);
  const formikRef = useRef();

  const [updateListing, { loading, data, error }] = useMutation(
    UPDATE_LISTING,
    {
      refetchQueries: [
        { query: GET_COMPANY_LISTINGS, variables: { companyId } },
      ],
      onCompleted() {
        resetOpenHouses();
        toast("success", "Listing successfully updated!");
        navigate(isAgentRole ? `/company/${companyId}/agent/listings` : `/company/${companyId}/listings`);
      },
      onError(e) {
        toast("error", e.message);
      },
    }
  );

  const handleSave = ({
    user,
    expiration,
    streetAddr,
    city,
    state,
    zip,
    beds,
    baths,
    price,
    yearBuilt,
    sqFt,
    lotSize,
    buyer_commission,
    acreage,
    notes,
    imageURLs,
    amenities,
    propertyType,
    propertyCategory,
    links,
  }) => {
    let agent = agents.find((a) => a.id === user);

    // Get office ID - use agent's default office or existing listing's office
    const officeId = agent?.defaultOffice?._id || listingData?.getListingById?.office?._id || null;

    updateListing({
      variables: {
        listingInput: {
          _id: listingId,
          user,
          expiration,
          streetAddr,
          city,
          state,
          zip,
          beds: propertyCategory === "land" ? 0 : beds,
          baths: propertyCategory === "land" ? 0 : baths,
          price,
          sqFt,
          notes,
          imageURLs,
          buyer_commission,
          amenities,
          propertyType,
          propertyCategory,
          lotSize: parseInt(lotSize),
          acreage: parseInt(acreage),
          yearBuilt: parseInt(yearBuilt),
          office: officeId,
          company: companyId,
          openHouses: isOpenHouse ? getFormattedOpenHouses() : [],
          links,
        },
      },
    });
  };

  useEffect(() => {
    if (agentData) {
      let filteredAgents = agentData.getCompanyUsers.filter(
        (a) =>
          ["AGENT", "ADMIN 1"].includes(a.role.name) && a.deletedAt === null
      );

      // If current user is an AGENT, only show themselves in the dropdown
      if (isAgentRole && currentUser?.id) {
        filteredAgents = filteredAgents.filter((a) => a.id === currentUser.id);
      }

      setAgents(filteredAgents);
    }
  }, [agentData, isAgentRole, currentUser?.id]);

  useEffect(() => {
    if (listingData?.getListingById) {
      const listing = listingData.getListingById;

      if (listing.openHouses?.length) {
        setOpenHouses(hydrateOpenHouseRows(listing.openHouses));
        setIsOpenHouse(true);
      }

      const date = listing.expiration
        ? new Date(parseInt(listing.expiration))
        : new Date();

      setInitialValues({
        ...initialValues,
        ...listing,
        amenities: listing.amenities?.map((a) => a._id) || [],
        expiration: date.toISOString().split("T")[0],
        user: listing.user?.id || "",
        links: listing.links?.map((i) => ({
          title: i.title,
          url: i.url,
        })) || [],
      });
    }
  }, [listingData]);

  if (agentLoad || listingLoad) return <Spinner />;

  return (
    <div id="Listing">
      <Breadcrumb pageName="Update a Listing" />
      <div className="py-6">
        <Card>
          <ListingForm
            initialValues={initialValues}
            handleSave={handleSave}
            type="edit"
            agents={agents}
            formikRef={formikRef}
            companyId={companyId}
            loading={loading}
            isAgentRole={isAgentRole}
          />
        </Card>
      </div>
    </div>
  );
};

export default UpdateListing;
